/**
 * Load the hero aircraft — the real glTFs, fetched when an aeroplane is chosen and not before.
 *
 * ## Why this is lazy, and why it is optional
 *
 * The payload budget is 6 MB gzipped for the *initial* load, and the three aircraft are 3.9, 2.3
 * and 1.9 MB on their own. Downloading all three to fly one would spend the whole budget on two
 * aeroplanes nobody asked for, so the model is fetched on selection and `GLTFLoader` itself comes
 * in through a dynamic `import()` so the parser is not in the entry chunk either.
 *
 * Optional because a missing or broken model must not ground the aeroplane. `airframes.ts` builds
 * a complete exterior for every aircraft from arithmetic; that geometry stays the fallback, and
 * everything here either succeeds and replaces it or fails and leaves it alone. There is no state
 * in between — `loadAircraftModel` resolves to `null` rather than throwing, and the scene only
 * swaps when it is handed something.
 *
 * ## The contract this reads
 *
 * Written by `tools/prep-aircraft.py`, documented in that file's header and in the README:
 *
 *   - render space, metres, nose −Z, up +Y, right +X, origin at the CG;
 *   - `BODY` for the fixed structure, and any other unnamed mesh with it;
 *   - `PROP` — origin at the hub, identity rotation, so spinning it is `rotation.z = angle`;
 *   - `PROP_DISC` — the blur disc at the same hub, shown instead of `PROP` above
 *     {@link PROP_BLUR_RPM};
 *   - `GEAR_*` — a retracting leg, carrying its retracted offset in glTF `extras.retractM`
 *     (render-space metres), which `GLTFLoader` puts in `userData`.
 *   - `NOZZLE` — an empty at the nozzle exit centre, carrying its exit radius in
 *     `extras.exitRadiusM`. Absent unless the aircraft has an afterburner.
 *
 * `NOZZLE` is the one that inverted a dependency. Until M7.6 `main.ts` hard-coded the plume at
 * `[0, 0.1, 4.6]` and the *model* was anchored to land its nozzle there — which is why the Vandal's
 * origin sat 68 % aft along its airframe where an F-16's centre of gravity is nearer 55 %. Now the
 * model states where its own nozzle is and the effect follows, so the origin is free to be the
 * centre of gravity, which is what every other subsystem already assumes it is.
 *
 * `updateFixed` / `updateFrame`: neither. Loading only; the scene drives what comes back.
 */

import {
  Box3,
  Group,
  Mesh,
  Object3D,
  Vector3,
  type BufferGeometry,
  type Material,
} from 'three';
import { PRODUCT_NAME } from '../brand.js';
import type { NozzleAnchor, RetractingPart } from './airframes.js';

/**
 * Shaft speed above which the blades are replaced by a disc [rpm].
 *
 * A three-blade propeller at 800 rpm passes a given point 40 times a second. No display resolves
 * that and no renderer should pretend to: past this the honest picture is the one a camera takes,
 * which is a translucent disc. Below it — starting, shutting down, feathered on a dead engine —
 * the blades are individually visible and have to be drawn, because "the propeller has stopped" is
 * information the pilot is entitled to.
 */
export const PROP_BLUR_RPM = 800;

/** What a loaded model hands back. Deliberately the same shape `airframes.ts` returns. */
export interface AircraftModel {
  group: Group;
  /** The blades. Null on the jet. */
  propeller: Object3D | null;
  /** The blur disc shown in place of {@link propeller}. Null when the model has none. */
  propellerDisc: Object3D | null;
  retractingParts: readonly RetractingPart[];
  /** Where this model's reheat plume starts. Null unless the model carries a `NOZZLE` node. */
  nozzle: NozzleAnchor | null;
  geometries: readonly BufferGeometry[];
  materials: readonly Material[];
  /** Span as measured off the loaded geometry [m], for the log line and the tests. */
  measuredSpanM: number;
  triangles: number;
}

/** Where a normalised model lives. One file, textures embedded, so an aircraft is one request. */
export function aircraftModelUrl(id: string): string {
  return `${import.meta.env.BASE_URL}models/${id}/model.glb`;
}

/**
 * Fetch and adopt an aircraft's exterior.
 *
 * Resolves to `null` for every failure — a 404 because the model was never baked, a parse error, a
 * network drop — and logs why. The caller keeps the built airframe in that case, which is the
 * whole reason this cannot throw.
 */
export async function loadAircraftModel(id: string): Promise<AircraftModel | null> {
  const url = aircraftModelUrl(id);
  try {
    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
    const gltf = await new GLTFLoader().loadAsync(url);
    return adoptAircraftModel(gltf.scene, id);
  } catch (error) {
    console.warn(`${PRODUCT_NAME}: no exterior model for "${id}" (${url}) — using the built airframe.`, error);
    return null;
  }
}

/**
 * Turn a parsed glTF scene into the handles the renderer indexes into.
 *
 * Split out from the fetch so it can be exercised without a network or a GPU: everything here is
 * scene-graph arithmetic, which is exactly the part with contract assumptions in it.
 */
export function adoptAircraftModel(scene: Object3D, id: string): AircraftModel {
  const group = new Group();
  group.name = `${id}-model`;
  group.add(scene);

  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  let triangles = 0;

  const propeller = scene.getObjectByName('PROP') ?? null;
  const propellerDisc = scene.getObjectByName('PROP_DISC') ?? null;

  scene.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const geometry = object.geometry as BufferGeometry;
    geometries.push(geometry);
    for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
      if (material !== null && material !== undefined) materials.push(material);
    }
    const index = geometry.getIndex();
    const position = geometry.getAttribute('position');
    triangles += index !== null ? index.count / 3 : (position?.count ?? 0) / 3;

    /*
     * The blur disc is the one thing here that must not cast a shadow. It stands in for blades
     * that are *mostly air* — a solid disc on the ground under a taxiing aeroplane reads as a
     * dinner plate, and the real propeller's shadow is a flicker nobody would notice missing.
     */
    object.castShadow = object !== propellerDisc && !isDescendantOf(object, propellerDisc);
    /*
     * The aeroplane casts shadows and does not receive them, which is what `airframes.ts` has
     * always done and is not an oversight there.
     *
     * The sun's shadow map is a cascade fitted around the camera's focus — kilometres of terrain
     * per cascade. At that scale a 15 m aeroplane occupies a handful of depth samples, so with
     * `receiveShadow` on it shadows *itself* almost everywhere: measured over the San Francisco
     * hills the jet rendered as a black silhouette against correctly-exposed terrain, and over
     * open water — outside the cascade — it rendered correctly light grey. Same model, same light,
     * same frame budget; the only difference was whether the shadow map covered it.
     */
    object.receiveShadow = false;
  });

  // The blades and the disc are alternatives, never both. Start on the blades: an aeroplane on the
  // ramp has a stopped propeller, and `setShaftSpeed` moves it the moment the engine turns.
  if (propellerDisc !== null) propellerDisc.visible = false;

  const retractingParts: RetractingPart[] = [];
  let gearNodesSeen = 0;
  scene.traverse((object) => {
    if (!object.name.startsWith('GEAR_')) return;
    gearNodesSeen += 1;
    /*
     * `extras` is what marks a leg, not the name.
     *
     * A leg with two materials arrives as a `Group` called `GEAR_LEFT` holding meshes called
     * `GEAR_LEFT_1` and `GEAR_LEFT_2` — three nodes whose names all start with `GEAR_`, of which
     * exactly one is the thing that moves. Skipping the ones without a retraction vector is what
     * keeps the strut and the tyre travelling with their leg instead of on their own.
     */
    const retract = object.userData?.['retractM'] as unknown;
    if (!Array.isArray(retract) || retract.length !== 3 || !retract.every((v) => typeof v === 'number')) {
      return;
    }
    const down = object.position.clone();
    retractingParts.push({
      mesh: object as Mesh,
      down,
      up: down.clone().add(new Vector3(retract[0], retract[1], retract[2])),
    });
  });
  if (gearNodesSeen > 0 && retractingParts.length === 0) {
    console.warn(
      `${PRODUCT_NAME}: ${id} has GEAR_* nodes but none carries extras.retractM — the gear will not retract. ` +
        'Re-run `npm run prep:aircraft`.',
    );
  }

  /*
   * The nozzle anchor, if this aeroplane has one.
   *
   * `position` rather than a world transform: `NOZZLE` is exported as a top-level node beside
   * `BODY`, so its local translation *is* the render-space station, and reading it that way keeps
   * the value independent of where the group has been parented by the time anyone asks.
   *
   * A `NOZZLE` without a usable radius is treated as no nozzle at all rather than as a nozzle of
   * some default width. A plume drawn wider than the pipe it comes out of is the single detail
   * that gives the effect away, so guessing is worse than the aeroplane the fallback airframe
   * already knows how to draw.
   */
  const nozzleNode = scene.getObjectByName('NOZZLE') ?? null;
  let nozzle: NozzleAnchor | null = null;
  if (nozzleNode !== null) {
    const exitRadiusM = nozzleNode.userData?.['exitRadiusM'] as unknown;
    if (typeof exitRadiusM === 'number' && Number.isFinite(exitRadiusM) && exitRadiusM > 0) {
      nozzle = { positionM: nozzleNode.position.clone(), exitRadiusM };
    } else {
      console.warn(
        `${PRODUCT_NAME}: ${id} has a NOZZLE node but no usable extras.exitRadiusM — the reheat ` +
          'plume will stay on the built airframe. Re-run `npm run prep:aircraft`.',
      );
    }
  }

  /*
   * Span is measured with the gear *excluded*, for the same reason `prep-aircraft.py` excludes it:
   * a leg hanging below the wing does not change how wide the aeroplane is, and a main leg that
   * retracts inboard would make the measurement depend on whether it happened to be up.
   */
  const box = new Box3();
  group.updateMatrixWorld(true);
  const legs = new Set(retractingParts.map((part) => part.mesh));
  scene.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    for (const leg of legs) if (object === leg || isDescendantOf(object, leg)) return;
    box.expandByObject(object);
  });

  return {
    group,
    propeller,
    propellerDisc,
    retractingParts,
    nozzle,
    geometries,
    materials,
    measuredSpanM: box.isEmpty() ? 0 : box.max.x - box.min.x,
    triangles,
  };
}

/**
 * Show the blades or the disc, according to shaft speed [rpm].
 *
 * A no-op for an exterior that has only one of the two — the built airframes have no separate
 * blades to hide, and the jet has neither.
 */
export function applyShaftSpeed(
  exterior: { propeller: Object3D | null; propellerDisc: Object3D | null },
  rpm: number,
): void {
  const { propeller, propellerDisc } = exterior;
  if (propeller === null || propellerDisc === null) return;
  const blurred = rpm >= PROP_BLUR_RPM;
  propeller.visible = !blurred;
  propellerDisc.visible = blurred;
}

/**
 * Put the gear where the flight model says it is, 0 up to 1 down.
 *
 * Shared by the built airframes and the loaded models because the retraction is modelled the same
 * way for both — a translation, driven by one already-smoothed number out of the flight model, so
 * the picture and the drag agree by construction rather than by two systems being tuned to match.
 */
export function applyGearFraction(parts: readonly RetractingPart[], fraction: number): number {
  const t = fraction <= 0 ? 0 : fraction >= 1 ? 1 : fraction;
  for (const part of parts) {
    part.mesh.position.lerpVectors(part.up, part.down, t);
    part.mesh.visible = t > 0.001;
  }
  return t;
}

function isDescendantOf(object: Object3D, ancestor: Object3D | null): boolean {
  if (ancestor === null) return false;
  for (let node = object.parent; node !== null; node = node.parent) {
    if (node === ancestor) return true;
  }
  return false;
}
